import { VISIT_ASSIGNMENT_CLINICIAN_RECOMMENDATIONS_QUERY } from '@/components/decisionSupport/queries/visitAssignment';
import {
    VisitAssignmentClinicianRecommendationsQuery,
    VisitAssignmentClinicianRecommendationsQueryVariables,
} from '@/components/decisionSupport/queries/visitAssignment.gql.types';
import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';

import { VisitOverrideState, buildVisitAssignmentInput } from '../utils/visitAssignmentInput.utils';

type ClinicianOutput = NonNullable<
    VisitAssignmentClinicianRecommendationsQuery['decisionSupport']['visitAssignment']['clinicianRecommendations']
>;
type ClinicianResult = ClinicianOutput['results'][number];
export type AssignmentClinicianRecommendation = ClinicianResult['clinicianRecommendation'];

export type UseAssignmentClinicianRecsResult = {
    // Endpoint-level factor/constraint definitions — identical across all visits.
    definitions: ClinicianOutput['definitions'] | null;
    // Per-visit recommendations keyed by visit ID for O(1) lookup.
    resultsByVisitId: ReadonlyMap<string, ClinicianResult>;
    loading: boolean;
    error: ErrorLike | undefined;
};

const EMPTY_RESULTS = new Map<string, ClinicianResult>();

// Fires the clinician-recommendations query (date held fixed) for the visit queue.
export const useAssignmentClinicianRecs = (
    visits: VisitOverrideState[],
): UseAssignmentClinicianRecsResult => {
    const input = useMemo(() => buildVisitAssignmentInput(visits), [visits]);

    const { data, loading, error } = useQuery<
        VisitAssignmentClinicianRecommendationsQuery,
        VisitAssignmentClinicianRecommendationsQueryVariables
    >(VISIT_ASSIGNMENT_CLINICIAN_RECOMMENDATIONS_QUERY, {
        variables: { input },
        skip: input.visits.length === 0,
        fetchPolicy: 'network-only',
    });

    const output = data?.decisionSupport.visitAssignment.clinicianRecommendations ?? null;

    const resultsByVisitId = useMemo(() => {
        if (!output) {
            return EMPTY_RESULTS;
        }
        const map = new Map<string, ClinicianResult>();
        output.results.forEach((r) => map.set(r.visitId, r));
        return map;
    }, [output]);

    return {
        definitions: output?.definitions ?? null,
        resultsByVisitId,
        loading,
        error,
    };
};
